import { Button, UIHeading2, UIText1, UIText2 } from '@cian/ui-kit';
import { IconActionCopy16, IconCommunicationPhone16, IconFunctionalClock16 } from '@cian/ui-kit-design-tokens/icons';

import { type FC, type SyntheticEvent, useCallback } from 'react';

import { useCAForCard } from '@/microfrontends/listing/shared/hooks/card/useCAForCard';

import { Link } from '@/packages/shared-ui';
import { memoWithDisplayName } from '@/packages/utils';
import { stopPropagation } from '@/packages/utils/event';

import styles from './ContactsModalContent.css';

type CopyTextButtonProps = {
  text: string;
  onCopy?(text: string): void;
  render: ({ onClick }: { onClick: (e: SyntheticEvent) => void }) => JSX.Element;
};

const CopyTextButton = memoWithDisplayName<CopyTextButtonProps>(({ render, text, onCopy }) => {
  const ca = useCAForCard();

  const handleClick = useCallback(
    (e: SyntheticEvent): void => {
      e.stopPropagation();

      ca({ action: 'copy_number', category: 'Listing' }, { label: true });

      try {
        navigator.clipboard.writeText(text);
      } catch (_) {
        const input = document.createElement('input');
        input.value = text;
        input.style.position = 'absolute';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        input.setSelectionRange(0, 99999);

        document.execCommand('copy');

        document.body.removeChild(input);
      }
      onCopy?.(text);
    },
    [onCopy, text, ca],
  );

  return render({ onClick: handleClick });
}, 'CopyTextButton');

type ContactModalContent = {
  publisher: string;
  onCopy(phone: string): void;
  workHours?: {
    /** Время начала работы **/
    workFrom: string;
    /** Время окончания работы **/
    workTo: string;
  };
  phones: {
    raw: string;
    formatted: string;
  }[];
};

export const ContactsModalContent: FC<ContactModalContent> = ({ publisher, phones, workHours, onCopy }) => {
  return (
    <div className={styles['wrapper']} data-testid="publisher.phoneModal">
      <div className={styles['publisher']}>
        <UIText1>{publisher}</UIText1>
      </div>
      <div className={styles['phones']}>
        {phones.map((phone, index) => (
          <div className={styles['phone-row']} key={index}>
            <UIHeading2>
              <Link
                behaviour="hover_black_to_primary"
                display="inline"
                href={`tel:${phone.raw}`}
                onClick={stopPropagation}
              >
                {phone.formatted}
              </Link>
            </UIHeading2>

            <div className={styles['button']}>
              <CopyTextButton
                render={({ onClick }) => (
                  <Button
                    beforeIcon={<IconActionCopy16 color="current_color" />}
                    size="XS"
                    theme="fill_secondary"
                    onClick={onClick}
                  />
                )}
                text={phone.raw}
                onCopy={onCopy}
              ></CopyTextButton>
            </div>
          </div>
        ))}
      </div>
      <div className={styles['disclamer']}>
        {workHours && (
          <div className={styles['disclamer-item']}>
            <div className={styles['disclamer-icon']}>
              <IconFunctionalClock16 color="gray60_100" />
            </div>
            <UIText2 color="text-primary-default">
              Агент работает с {workHours.workFrom} до {workHours.workTo}
            </UIText2>
          </div>
        )}
        <div className={styles['disclamer-item']}>
          <div className={styles['disclamer-icon']}>
            <IconCommunicationPhone16 color="gray60_100" />
          </div>
          <UIText2 color="text-primary-default">Номер только для звонков, сообщения не дойдут</UIText2>
        </div>
      </div>
    </div>
  );
};
